import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { fmtEuro, MONTHS_EL_SHORT } from "@/lib/format";
import { exportElementToPdf } from "@/lib/pdf";
import { TrendingUp, FileDown, CalendarRange, Check } from "lucide-react";
import { BudgetVarianceAlerts } from "@/components/finance/BudgetVarianceAlerts";

const SECTION_ORDER = ["revenue", "dept", "undistributed", "fixed"];
const SUBTOTALS = [["Κέρδος τμημάτων", "dept_profit"], ["GOP (Λειτ. αποτέλεσμα)", "gop"], ["Καθαρό αποτέλεσμα", "net_profit"]];
const YEAR = 2026;
const r2 = (n) => Math.round((n || 0) * 100) / 100;

/** Red when a cost is over budget or revenue is under budget. */
const tone = (budget, actual, isCost) => {
  if (Math.abs((actual || 0) - (budget || 0)) < 0.5) return "text-[#6F6A60]";
  const bad = isCost ? actual > budget : actual < budget;
  return bad ? "text-[#991B1B] font-semibold" : "text-[#6E7A63] font-semibold";
};

const Cells = ({ budget, actual, isCost, dim }) => (
  <>
    <td className={`px-2 py-2 text-right tabular ${dim ? "text-[#8A8577]" : ""}`}>{fmtEuro(budget)}</td>
    <td className={`px-2 py-2 text-right tabular ${dim ? "text-[#8A8577]" : ""}`}>{fmtEuro(actual)}</td>
    <td className={`px-2 py-2 text-right tabular ${tone(budget, actual, isCost)}`}>{fmtEuro(r2(budget - actual))}</td>
  </>
);

export default function PLVariance({ embedded = false }) {
  const [months, setMonths] = useState([new Date().getMonth() + 1]);
  const [custom, setCustom] = useState(null); // {from, to} when custom period is active
  const [cc, setCc] = useState("all");
  const [pdfBusy, setPdfBusy] = useState(false);
  const sheet = React.useRef(null);

  const { data: meta } = useQuery({ queryKey: ["finance-accounts"], queryFn: async () => (await api.get("/finance/accounts")).data });
  const { data: pl, isLoading } = useQuery({
    queryKey: ["pl-year", YEAR, cc],
    queryFn: async () => (await api.get(`/finance/pl?year=${YEAR}&cost_center=${cc}`)).data,
    enabled: !custom,
  });
  const { data: period, isLoading: loadingPeriod } = useQuery({
    queryKey: ["pl-period", custom?.from, custom?.to, cc],
    queryFn: async () => (await api.get(`/finance/pl-period?date_from=${custom.from}&date_to=${custom.to}&cost_center=${cc}`)).data,
    enabled: !!custom,
  });

  const toggleMonth = (m) => {
    setCustom(null);
    setMonths((prev) => (prev.includes(m) ? (prev.length > 1 ? prev.filter((x) => x !== m) : prev) : [...prev, m].sort((a, b) => a - b)));
  };

  const sel = months;
  const ytdMonths = useMemo(() => Array.from({ length: Math.max(...sel) }, (_, i) => i + 1), [sel]);
  const sum = (obj, keys) => r2(keys.reduce((s, m) => s + (obj?.[String(m)] || 0), 0));

  /** Uniform view model for both modes: groups of columns + line values. */
  const view = useMemo(() => {
    if (custom) {
      if (!period) return null;
      const groups = [{ key: "period", label: `${period.date_from} → ${period.date_to}` },
        { key: "ytd", label: `YTD (από ${period.ytd_from})`, dim: true }];
      const line = (r) => ({ period: [r.budget, r.actual], ytd: [r.ytd_budget, r.ytd_actual] });
      const secOf = (s) => ({ period: [period.sections[s]?.budget, period.sections[s]?.actual],
        ytd: [period.ytd.sections[s]?.budget, period.ytd.sections[s]?.actual] });
      const subOf = (k) => ({ period: [period.subtotals[k]?.budget, period.subtotals[k]?.actual],
        ytd: [period.ytd.subtotals[k]?.budget, period.ytd.subtotals[k]?.actual] });
      return { groups, rows: period.rows, line, secOf, subOf, labels: period.section_labels,
        title: `${period.date_from} – ${period.date_to}` };
    }
    if (!pl) return null;
    const groups = [
      ...sel.map((m) => ({ key: `m${m}`, label: MONTHS_EL_SHORT[m], months: [m] })),
      ...(sel.length > 1 ? [{ key: "total", label: "Σύνολο επιλογής", months: sel, strong: true }] : []),
      { key: "ytd", label: `YTD Ιαν–${MONTHS_EL_SHORT[Math.max(...sel)]}`, months: ytdMonths, dim: true },
    ];
    const pick = (b, a) => Object.fromEntries(groups.map((g) => [g.key, [sum(b, g.months), sum(a, g.months)]]));
    return {
      groups,
      rows: pl.rows,
      line: (r) => pick(r.budget_months, r.actual_months),
      secOf: (s) => pick(pl.sections[s]?.budget, pl.sections[s]?.actual),
      subOf: (k) => pick(pl.subtotals[k]?.budget, pl.subtotals[k]?.actual),
      labels: pl.section_labels,
      title: sel.map((m) => MONTHS_EL_SHORT[m]).join(", ") + ` ${YEAR}`,
    };
  }, [custom, period, pl, sel, ytdMonths]);

  const rowsBySection = useMemo(() => {
    const m = {};
    (view?.rows || []).forEach((r) => { (m[r.section] = m[r.section] || []).push(r); });
    return m;
  }, [view]);

  const downloadPdf = async () => {
    setPdfBusy(true);
    try { await exportElementToPdf(sheet.current, `TAF_Apotelesmata_${YEAR}.pdf`, "landscape"); }
    finally { setPdfBusy(false); }
  };

  const mainKey = view?.groups?.find((g) => g.strong)?.key || view?.groups?.[0]?.key;
  const kpi = (vals) => (vals ? vals[mainKey] : [0, 0]);
  const Kpi = ({ label, vals, isCost }) => {
    const [b, a] = kpi(vals) || [0, 0];
    return (
      <div className="rounded-2xl bg-white border border-[#E3DED4] p-5" data-testid={`plv-kpi-${label}`}>
        <div className="text-[10px] uppercase tracking-widest text-[#6F6A60]">{label}</div>
        <div className="font-serif text-2xl mt-2 text-[#45423C] tabular">{fmtEuro(a)}</div>
        <div className={`text-xs mt-1 tabular ${tone(b, a, isCost)}`}>Πρβλ. {fmtEuro(b)} · {fmtEuro(r2(b - a))}</div>
      </div>
    );
  };

  const busy = custom ? loadingPeriod : isLoading;

  return (
    <div className="space-y-6" data-testid="pl-variance-page">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          {!embedded && <><p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Έλεγχος απόκλισης</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1">Πραγματικά vs Προϋπολογισμός</h1></>}
          <p className="text-[#6F6A60] text-sm mt-1">
            Επιλέξτε έναν ή περισσότερους μήνες — κάθε μήνας εμφανίζεται σε δική του στήλη και στο τέλος το σύνολο από αρχή έτους (YTD).
            Με <span className="text-[#991B1B] font-semibold">κόκκινο</span> τα έξοδα πάνω από τον προϋπολογισμό και τα έσοδα κάτω από τον στόχο.
          </p>
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <select data-testid="plv-cc" value={cc} onChange={(e) => setCc(e.target.value)}
            className="rounded-xl border border-[#E3DED4] bg-white px-4 py-2.5 text-sm outline-none focus:border-[#45423C]">
            <option value="all">Όλα τα cost centers</option>
            {(meta?.cost_centers || []).map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
          <button data-testid="plv-pdf" onClick={downloadPdf} disabled={pdfBusy}
            className="inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] bg-white px-4 py-2.5 text-sm font-semibold text-[#45423C] hover:border-[#B9AE9A]">
            <FileDown className="h-4 w-4" /> {pdfBusy ? "Δημιουργία…" : "Λήψη PDF"}
          </button>
        </div>
      </div>

      <BudgetVarianceAlerts year={YEAR} />

      <div className="rounded-2xl bg-white border border-[#E3DED4] p-3 space-y-2" data-testid="plv-tabs">
        <div className="flex flex-wrap items-center gap-1.5">
          {MONTHS_EL_SHORT.slice(1).map((label, i) => {
            const m = i + 1;
            const on = !custom && sel.includes(m);
            return (
              <button key={label} data-testid={`plv-tab-${m}`} onClick={() => toggleMonth(m)}
                className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                  on ? "bg-[#45423C] text-white" : "text-[#6F6A60] hover:bg-[#F1EDE6]"}`}>
                {on && <Check className="h-3 w-3" />}{label}
              </button>
            );
          })}
          <span className="mx-1 h-5 w-px bg-[#E3DED4]" />
          <button data-testid="plv-tab-year" onClick={() => { setCustom(null); setMonths(Array.from({ length: 12 }, (_, i) => i + 1)); }}
            className="rounded-full px-3 py-1.5 text-xs font-semibold text-[#6F6A60] hover:bg-[#F1EDE6]">
            Όλο το {YEAR}
          </button>
          <button data-testid="plv-tab-ytd" onClick={() => { setCustom(null); setMonths([new Date().getMonth() + 1]); }}
            className="rounded-full px-3 py-1.5 text-xs font-semibold text-[#6F6A60] hover:bg-[#F1EDE6]">
            Τρέχων μήνας
          </button>
          <button data-testid="plv-tab-custom"
            onClick={() => setCustom(custom ? null : { from: `${YEAR}-01-01`, to: new Date().toISOString().slice(0, 10) })}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${
              custom ? "bg-[#45423C] text-white" : "text-[#6F6A60] hover:bg-[#F1EDE6]"}`}>
            <CalendarRange className="h-3.5 w-3.5" /> Προσαρμοσμένη περίοδος
          </button>
          {custom && (
            <div className="flex items-center gap-2 ml-1">
              <input data-testid="plv-from" type="date" value={custom.from} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))}
                className="rounded-lg border border-[#E3DED4] px-2 py-1.5 text-xs outline-none focus:border-[#45423C]" />
              <span className="text-xs text-[#6F6A60]">έως</span>
              <input data-testid="plv-to" type="date" value={custom.to} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))}
                className="rounded-lg border border-[#E3DED4] px-2 py-1.5 text-xs outline-none focus:border-[#45423C]" />
            </div>
          )}
        </div>
        {!custom && (
          <p className="text-[11px] text-[#8A8577]">
            Πατήστε μήνες για να τους προσθέσετε ή να τους αφαιρέσετε από τη σύγκριση · επιλεγμένοι: {sel.map((m) => MONTHS_EL_SHORT[m]).join(", ")}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Έσοδα" vals={view?.secOf("revenue")} />
        <Kpi label="Κόστος τμημάτων" vals={view?.secOf("dept")} isCost />
        <Kpi label="GOP" vals={view?.subOf("gop")} />
        <Kpi label="Καθαρό αποτέλεσμα" vals={view?.subOf("net_profit")} />
      </div>

      <div ref={sheet} className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E3DED4] flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-[#C86235]" />
          <h2 className="font-medium text-[#45423C]">Αποτελέσματα ανά γραμμή · {view?.title || "—"}</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead>
              <tr className="bg-[#45423C] text-left text-[10px] uppercase tracking-wider text-white/80">
                <th rowSpan={2} className="px-3 py-2.5 sticky left-0 bg-[#45423C]">Γραμμή</th>
                {(view?.groups || []).map((g) => (
                  <th key={g.key} colSpan={3} data-testid={`plv-group-${g.key}`}
                    className={`px-2 py-2 text-center border-l border-white/15 ${g.dim ? "bg-[#56524A]" : ""} ${g.strong ? "bg-[#6E7A63]" : ""}`}>
                    {g.label}
                  </th>
                ))}
              </tr>
              <tr className="bg-[#56524A] text-left text-[9px] uppercase tracking-wider text-white/70">
                {(view?.groups || []).map((g) => (
                  <React.Fragment key={g.key}>
                    <th className="px-2 py-1.5 text-right border-l border-white/15">Πρβλ.</th>
                    <th className="px-2 py-1.5 text-right">Πραγμ.</th>
                    <th className="px-2 py-1.5 text-right">Διαφ.</th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {busy && <tr><td colSpan={1 + (view?.groups?.length || 1) * 3} className="px-4 py-8 text-center text-[#6F6A60]">Φόρτωση…</td></tr>}
              {SECTION_ORDER.map((s) => {
                const secVals = view?.secOf(s);
                return (
                  <React.Fragment key={s}>
                    <tr className="bg-[#F1EDE6]" data-testid={`plv-section-${s}`}>
                      <td className="px-3 py-2 font-semibold text-[11px] uppercase tracking-wider text-[#45423C] sticky left-0 bg-[#F1EDE6]">
                        {view?.labels?.[s] || s}
                      </td>
                      {(view?.groups || []).map((g) => (
                        <Cells key={g.key} budget={secVals?.[g.key]?.[0]} actual={secVals?.[g.key]?.[1]}
                          isCost={s !== "revenue"} dim={g.dim} />
                      ))}
                    </tr>
                    {(rowsBySection[s] || []).map((r) => {
                      const vals = view.line(r);
                      return (
                        <tr key={r.code} className="border-t border-[#E3DED4] hover:bg-[#F7F5F1]" data-testid={`plv-row-${r.code}`}>
                          <td className="px-3 py-2 sticky left-0 bg-white">
                            <span className="font-mono text-[10px] text-[#8A8577] mr-2">{r.code}</span>
                            <span className="text-[#45423C]">{r.name}</span>
                          </td>
                          {(view.groups || []).map((g) => (
                            <Cells key={g.key} budget={vals[g.key]?.[0]} actual={vals[g.key]?.[1]}
                              isCost={s !== "revenue"} dim={g.dim} />
                          ))}
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}

              {SUBTOTALS.map(([label, key]) => {
                const vals = view?.subOf(key);
                return (
                  <tr key={key} className="border-t-2 border-[#45423C] bg-[#45423C] text-white" data-testid={`plv-sub-${key}`}>
                    <td className="px-3 py-2.5 font-semibold sticky left-0 bg-[#45423C]">{label}</td>
                    {(view?.groups || []).map((g) => {
                      const [b, a] = vals?.[g.key] || [0, 0];
                      return (
                        <React.Fragment key={g.key}>
                          <td className="px-2 py-2.5 text-right tabular text-white/70 border-l border-white/15">{fmtEuro(b)}</td>
                          <td className="px-2 py-2.5 text-right tabular font-semibold">{fmtEuro(a)}</td>
                          <td className={`px-2 py-2.5 text-right tabular font-semibold ${a - b < 0 ? "text-[#F3B3A6]" : "text-[#C9D6C0]"}`}>
                            {fmtEuro(r2(b - a))}
                          </td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
